import React, { useEffect, useState } from "react";
import {
  Card,
  Tabs,
  List,
  Button,
  Tag,
  Typography,
  DatePicker,
  Space,
  Tooltip,
  Drawer,
  Modal,
  message,
  Dropdown,
  InputNumber,
  Grid,
} from "antd";
import dayjs from "dayjs";
import { fetchTasksApi, completeTaskApi, snoozeTaskApi, rescheduleTaskApi } from "../../../apiservice/crm";
import TaskDrawer from "./components/TaskDrawer";
import { useDispatch, useSelector } from "react-redux";
import { fetchLeads } from "../Leads/leadThunk";
import { fetchDeals } from "../Deals/dealThunk";
import { useNavigate } from "react-router-dom";
import { ArrowLeftOutlined, QuestionCircleOutlined, WhatsAppOutlined } from "@ant-design/icons";
import CallTab from "../Leads/components/CallTab";
import { convertToCompanyTimezoneObject, formatDayjsToUtc  } from "../../../lib/functions";
import { resolveWhatsAppNumber } from "../../../lib/phone";
import "../crmSharedStyle.css";

const { Text } = Typography;
const { Title } = Typography;
const { useBreakpoint } = Grid;

const TaskTabs = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [tasks, setTasks] = useState([]);
  const [taskDrawer, setTaskDrawer] = useState(false);
  const [loading, setLoading] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [customSnoozeOpen, setCustomSnoozeOpen] = useState(false);
  const [customMinutes, setCustomMinutes] = useState(30);
  const [selectedTask, setSelectedTask] = useState(null);
  const [callModalTask, setCallModalTask] = useState(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { list: leads } = useSelector(s => s.leads);
  const { list: deals } = useSelector(s => s.deals);
  const load = async () => {
    setLoading(true);
    const res = await fetchTasksApi({});
    setTasks(res.data || []);
    setLoading(false);
  };



  useEffect(() => {
    load();
    const interval = setInterval(load, 3600000); // 1hr
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!leads?.length) {
      dispatch(fetchLeads());
    }
    if (!deals?.length) {
      dispatch(fetchDeals());
    }
  }, [leads?.length, deals?.length, dispatch ]);

  const markDone = async (task_id) => {
    await completeTaskApi({ task_id });
    load();
  };

  const snoozeTask = async (taskId, minutes) => {
    await snoozeTaskApi({
      task_id: taskId,
      minutes,
    });

    message.success(
      `Task snoozed by ${minutes} minute(s)`
    );

    load();
  };

  const snoozeMenu = (task) => ({
    items: [
      {
        key: "30",
        label: "30 minutes",
        onClick: () => snoozeTask(task.task_id, 30),
      },
      {
        key: "60",
        label: "1 hour",
        onClick: () => snoozeTask(task.task_id, 60),
      },
      {
        key: "120",
        label: "2 hours",
        onClick: () => snoozeTask(task.task_id, 120),
      },
      {
        type: "divider",
      },
      {
        key: "custom",
        label: "Custom...",
        onClick: () => {
          setSelectedTask(task);
          setCustomMinutes(30);
          setCustomSnoozeOpen(true);
        },
      },
    ],
  });

  const count = (type) => tasks.filter(t => t.bucket === type).length;  

  const renderList = (filter) => {
    const filtered =
      filter === "completed"
        ? // Most recently completed first -- there's no dedicated
          // completed_at column, but completeTask sets updated_at exactly
          // when a task is marked done, so it's a reliable proxy here.
          tasks
            .filter((t) => t.bucket === filter)
            .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
        : tasks
            .filter((t) => t.bucket === filter)
            .sort(
              (a, b) =>
                b.urgency - a.urgency ||
                new Date(a.due_date) -
                  new Date(b.due_date)
            );

    return (

      <List
        key={filter}
        className="crm-task-list"
        dataSource={filtered}
        pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: [10, 20, 50] }}
        locale={{
          emptyText:
            "No tasks. Create one to get started 🚀",
        }}
        loading={loading}
        renderItem={(task) => {
          const isOverdue =
            task.bucket === "overdue";

          return (
            <List.Item
              onClick={() =>
                navigate(
                  `/crm/tasks/${task.task_id}`
                )
              }
              style={{
                background: "#fff",
                borderRadius: 16,
                marginBottom: 14,
                padding: 18,
                cursor: "pointer",
                border: isOverdue
                  ? "1px solid #ffccc7"
                  : "1px solid #f0f0f0",
                boxShadow:
                  "0 2px 10px rgba(0,0,0,0.04)",
                transition: "0.2s",
              }}
            >
              <div style={{ width: "100%" }}>
                {/* TOP */}
                <div
                  style={{
                    display: "flex",
                    justifyContent:
                      "space-between",
                    alignItems: "center",
                    marginBottom: 10,
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: 700,
                      }}
                    >
                      {task.type}
                    </div>

                    <div
                      style={{
                        color: "#666",
                        marginTop: 4,
                      }}
                    >
                      {task.remarks ||
                        "No remarks"}
                    </div>
                  </div>

                  <Space>
                    <Tag
                      color={
                        task.bucket === "completed"
                        ? "green"
                        : task.bucket === "overdue"
                        ? "red"
                        : task.bucket === "today"
                        ? "orange"
                        : task.bucket === "cancelled"
                        ? "default"
                        : "blue"
                      }
                    >
                      {task.bucket}
                    </Tag>

                    <Tag
                      color={
                        task.priority === "high"
                          ? "red"
                          : task.priority === "medium"
                          ? "orange"
                          : "green"
                      }
                    >
                      {task.priority
                        ? task.priority.charAt(0).toUpperCase() +
                          task.priority.slice(1)
                        : "Low"}
                    </Tag>
                  </Space>
                </div>

                {/* MIDDLE */}
                <div
                  style={{
                    display: "flex",
                    gap: 30,
                    flexWrap: "wrap",
                    marginBottom: 10,
                  }}
                >
                  <div>
                    <div
                      style={{
                        color: "#888",
                        fontSize: 12,
                      }}
                    >
                      CUSTOMER
                    </div>

                    <div
                      style={{
                        fontWeight: 600,
                      }}
                    >
                      {task.customer_name}
                    </div>
                  </div>

                  <div>
                    <div
                      style={{
                        color: "#888",
                        fontSize: 12,
                      }}
                    >
                      PHONE
                    </div>

                    <div
                      style={{
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      {task.contact_no}
                      {task.contact_no && (
                        <WhatsAppOutlined
                          style={{ color: "#25D366", cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(`https://wa.me/${resolveWhatsAppNumber(task.contact_no, task.lead_country)}`);
                          }}
                        />
                      )}
                    </div>
                  </div>

                  <div>
                    <div
                      style={{
                        color: "#888",
                        fontSize: 12,
                      }}
                    >
                      DUE DATE
                    </div>

                    <div
                      style={{
                        fontWeight: 600,
                        color: isOverdue
                          ? "#ff4d4f"
                          : "",
                      }}
                    >
                      {convertToCompanyTimezoneObject(
                        task.due_date
                      ).format(
                        "DD MMM YYYY hh:mm A"
                      )}
                    </div>
                  </div>
                </div>

                {/* FOOTER */}
                <div
                  style={{
                    display: "flex",
                    justifyContent:
                      "space-between",
                    alignItems: "center",
                    marginTop: 8,
                  }}
                >
                  <div
                    style={{
                      color: "#888",
                      fontSize: 13,
                    }}
                  >
                    {convertToCompanyTimezoneObject(
                      task.due_date
                    ).fromNow()}
                  </div>

                  <div
                    style={{
                      color: "#1677ff",
                      fontWeight: 600,
                    }}
                  >
                    View Details →
                  </div>
                </div>

                {/* ACTION ROW */}
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 8,
                    marginTop: 12,
                    paddingTop: 12,
                    borderTop: "1px solid #f0f0f0",
                  }}
                >
                  {task.status !== "completed" && (
                    <Button
                      key="complete"
                      type="text"
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (task.type === "call" || task.type === "followup") {
                          setCallModalTask(task);
                        } else {
                          await completeTaskApi({ task_id: task.task_id });
                          load();
                        }
                      }}
                    >
                      ✅
                    </Button>
                  )}

                  {task.status === "pending" &&
                    (task.bucket === "today" || task.bucket === "overdue") && (
                      <Dropdown
                        menu={snoozeMenu(task)}
                        trigger={["click"]}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button
                          key="snooze"
                          type="text"
                          onClick={(e) => e.stopPropagation()}
                        >
                          ⏰
                        </Button>
                      </Dropdown>
                    )}

                  {task.status !== "completed" && (
                    <DatePicker
                      key="reschedule"
                      showTime={{ format: "HH:mm" }}
                      format="DD/MM/YYYY HH:mm"
                      style={{ flex: isMobile ? 1 : undefined, minWidth: 0 }}
                      placeholder="Reschedule"
                      popupClassName="reschedule-datetime-popup"
                      getPopupContainer={() => document.body}
                      onClick={(e) => e.stopPropagation()}
                      disabledDate={(current) =>
                        current && current.endOf("day").isBefore(dayjs())
                      }
                      onChange={async (date) => {
                        if (!date) return;
                        await rescheduleTaskApi({
                          task_id: task.task_id,
                          due_date: formatDayjsToUtc(date.second(0)),
                        });
                        load();
                      }}
                    />
                  )}
                </div>
              </div>
            </List.Item>
          );
        }}
      />
    );
  };

  return (
    <>
    <style>{`
      .crm-task-list .ant-list-item {
        flex-wrap: wrap;
      }
      .crm-task-list .ant-list-item-action {
        display: flex;
        flex-wrap: wrap;
        row-gap: 8px;
        margin-inline-start: 0 !important;
        padding-inline-start: 0 !important;
        justify-content: flex-start;
        width: 100%;
      }
      .crm-task-list .ant-list-item-action > li {
        padding-inline-start: 0 !important;
      }
      .crm-task-list .ant-list-item-action > li:first-child {
        padding-inline-start: 0 !important;
        margin-inline-start: 0 !important;
      }
      @media (max-width: 767px) {
        .reschedule-datetime-popup {
          max-width: 100vw !important;
          left: 8px !important;
          right: 8px !important;
        }
        .reschedule-datetime-popup .ant-picker-panel-container {
          max-width: calc(100vw - 16px) !important;
          max-height: 60vh !important;
          overflow: auto !important;
        }
        .reschedule-datetime-popup .ant-picker-datetime-panel {
          flex-wrap: wrap;
        }
        .reschedule-datetime-popup .ant-picker-time-panel {
          width: 100%;
        }
        .reschedule-datetime-popup .ant-picker-time-panel-column {
          width: 50%;
        }
      }
    `}</style>
    <Card
  styles={{
    header: {
      display: "flex",
      flexWrap: "wrap",
      gap: 8,
      rowGap: 8,
    },
    title: {
      whiteSpace: "normal",
      overflow: "visible",
      flex: isMobile ? "1 1 100%" : undefined,
      paddingInlineStart: isMobile ? 0 : undefined,
      marginInlineStart: isMobile ? 0 : undefined,
    },
    extra: {
      marginInlineStart: isMobile ? 0 : undefined,
    },
  }}
  title={
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
      }}
    >
      <div style={{ display: "flex", alignItems: "center" }}>
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(-1)} 
        style={{ marginRight: 8 }}
      />
      <Title level={4} style={{ margin: 0 }}>
        Tasks
      </Title>
      </div>

      <Tooltip title="Help">
        <Button
          type="text"
          icon={<QuestionCircleOutlined style={{ fontSize: 20 }} />}
          onClick={() => setHelpOpen(true)}
        />
      </Tooltip>
    </div>
  }
    extra={
        <Button type="primary" onClick={() => setTaskDrawer(true)}>
          + Add Task
        </Button>
      }
    >
      <Tabs
        defaultActiveKey="today"
        items={[
          {
            key: "today",
            label: `Today (${count("today")})`,
            children: renderList("today"),
          },
          {
            key: "upcoming",
            label: `Upcoming (${count("upcoming")})`,
            children: renderList("upcoming"),
          },
          {
            key: "overdue",
            label: `Overdue (${count("overdue")})`,
            children: renderList("overdue"),
          },
          {
            key: "completed",
            label: `Completed (${count("completed")})`,
            children: renderList("completed"),
          },
        ]}
      />

      <TaskDrawer
        open={taskDrawer}
        onClose={() => setTaskDrawer(false)}
        defaultLeadId={null}
        leads={leads || []}   // we'll fix below
        deals={deals || []}
        onSuccess={load}
      />

      <Modal
        title="Custom Snooze"
        open={customSnoozeOpen}
        onCancel={() => setCustomSnoozeOpen(false)}
        onOk={async () => {
          if (customMinutes < 1) {
            return message.warning(
              "Minutes should be greater than zero."
            );
          }

          await snoozeTask(
            selectedTask.task_id,
            customMinutes
          );

          setCustomSnoozeOpen(false);
        }}
      >
        <div style={{ marginBottom: 10 }}>
          Snooze for
        </div>

        <InputNumber
          min={1}
          max={1440}
          value={customMinutes}
          onChange={setCustomMinutes}
          style={{ width: "100%" }}
          addonAfter="minutes"
        />
      </Modal>


      <Drawer
        title="CRM Tasks Help"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        width={screens.md ? 430 : "100%"}
        className="crm-drawer"
        styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
      >
  <div style={{ lineHeight: 1.8 }}>
    <h2
      style={{
        fontSize: "18px",
        fontWeight: 600,
        marginBottom: "12px",
        marginLeft: "12px"
      }}
    >
      CRM Task Management
    </h2>

    <p style={{ color: "#555", marginBottom: "16px", marginLeft:17 }}>
      The CRM Tasks module is used to manage customer follow-ups,
      scheduled activities, reminders, and sales-related task workflows
      within the ERP CRM system. It helps organize daily sales operations,
      customer communication, and activity tracking.
    </p>

    <div
      style={{
        background: "#f5f5f5",
        padding: "14px",
        borderRadius: "8px",
        marginBottom: "16px",
      }}
    >
      <strong>Available Actions</strong>

      <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
        <li>View Today, Upcoming, and Overdue task lists</li>
        <li>Create new CRM tasks and follow-ups</li>
        <li>Mark tasks as completed</li>
        <li>Snooze tasks for later follow-up</li>
        <li>Reschedule tasks using date and time selection</li>
        <li>Track customer names and contact details</li>
        <li>Monitor task priorities and due dates</li>
        <li>Open detailed task information pages</li>
        <li>Manage lead and deal related activities</li>
        <li>Track customer communication workflows</li>
        <li>View task priority indicators and overdue alerts</li>
      </ol>
    </div>

    <div
      style={{
        background: "#fafafa",
        border: "1px solid #eee",
        padding: "14px",
        borderRadius: "8px",
      }}
    >
      <strong>Important Notes</strong>

      <ul
        style={{
          paddingLeft: "18px",
          marginTop: "10px",
          color: "#666",
        }}
      >
        <li>Overdue tasks are highlighted for quick identification.</li>
        <li>Task priorities help organize important follow-ups.</li>
        <li>Snooze functionality delays reminders temporarily.</li>
        <li>Rescheduling updates task due dates immediately.</li>
        <li>Tasks can be linked with CRM leads and deals.</li>
        <li>Task lists refresh automatically at regular intervals.</li>
        <li>CRM activities help maintain customer follow-up history.</li>
      </ul>
    </div>
  </div>
</Drawer>
<Modal
  title="Log Call Before Completing"
  open={!!callModalTask}
  onCancel={() => setCallModalTask(null)}
  footer={null}
  destroyOnClose
>
  {callModalTask && (
    <CallTab
      lead={{ id: callModalTask.lead_id }}
      deal={null}
      onAfterSubmit={async () => {
        await completeTaskApi({ task_id: callModalTask.task_id });
        setCallModalTask(null);
        load();
      }}
    />
  )}
</Modal>
    </Card>
    </>
  );
};

export default TaskTabs;